import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { ReactComponent as AnnotationIndicator } from 'icons/annotation-indicator.svg';
import {
  getMarkerPosition,
  getCalloutPosition,
} from 'constants/feature/tieout-element-utils';
import { useSelector } from 'react-redux';
import {
  STATEMENT_CONTENT_PANEL_ZOOM_BLOCK,
  STATEMENT_CONTENT_PANEL_ZOOM_LEFT_BLOCK,
} from 'constants/feature/statement-content-constants';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';

export const CALLOUT_MAX_WIDTH = '120px';

const TieoutElementCallouts = ({
  children,
  elementRef,
  onAnnotationClick,
  annotationPosition,
  getShowCalloutValue,
  isShowCallout,
  setShowCalloutVal,
  elementData,
  annotationDisplayFilter,
  id,
  ELEMENT_BLOCK,
  displayElementDetailsOnStatementPage,
  sectionId,
  leftSideView,
  numberOfSourceElementSelected,
  numberOfTargetElementSelected,
}) => {
  const data = useSelector((store) => store.data);
  const ui = useSelector((store) => store.ui);
  const {
    statementContent: { sectionsCache: { cache } = {} } = {},
    leftStatementContent: { leftSectionsCache: { leftCache } = {} } = {},
    statementSummary: { annotationFontSize: { fontSize } = {} } = {},
  } = data || {};

  const {
    statementPage: {
      zoom: zoomResult,
      leftZoom,
      rightZoom,
      modes: { contentViewMode },
    },
  } = ui;

  const pathName = useLocation().pathname;
  const _isSideBySideView = useMemo(
    () => pathName.includes(ROUTE_CONSTANTS.SIDE_BY_SIDE),
    [pathName],
  );

  const zoom = _isSideBySideView
    ? leftSideView
      ? leftZoom
      : rightZoom
    : zoomResult;

  const [tableControlsChanged, setTableControlsChanged] = useState(false);

  const [screenSize, setScreenSize] = useState(
    window.innerWidth * window.innerHeight,
  );

  const calloutRef = useRef(null);
  const markerRef = useRef(null);

  const elementSize =
    elementRef && elementRef.current
      ? window.getComputedStyle(elementRef.current).fontSize
      : 0;

  useEffect(() => {
    window.addEventListener('resize', () => {
      setScreenSize(window.innerWidth * window.innerHeight);
    });
    return () => {
      window.removeEventListener('resize', () => {
        setScreenSize(window.innerWidth * window.innerHeight);
      });
    };
  }, []);
  useEffect(() => {
    // Annotation Show or Hide
    setShowCalloutVal(
      getShowCalloutValue(elementData, annotationDisplayFilter),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    annotationPosition.position,
    elementData,
    annotationDisplayFilter.showAll,
    annotationDisplayFilter.workpaper,
    annotationDisplayFilter.tickmarks,
    annotationDisplayFilter.internalRef,
    annotationDisplayFilter.formula,
    annotationDisplayFilter.comfortLetter,
  ]);

  const [annotationStyle, setAnnotationStyle] = useState({
    marker: {},
    callout: {},
  });

  useEffect(() => {
    const tables = document.querySelectorAll(
      '.statement-content-segment table',
    );
    // Since we have table controls to change width of a specific table. We need to check if the it is being clicked.
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-fixed-width'
        ) {
          setTableControlsChanged((prevState) => !prevState);
        }
      }
    });

    tables &&
      !!tables.length &&
      tables.forEach((table) => {
        observer.observe(table, { attributes: true });
      });

    return () => {
      observer.disconnect();
    };
  }, []);

  //Need to get these positions to calculate the latest position on initial load of the document.
  const documentId = leftSideView
    ? STATEMENT_CONTENT_PANEL_ZOOM_LEFT_BLOCK
    : STATEMENT_CONTENT_PANEL_ZOOM_BLOCK;
  const parentElement = document && document.getElementById(documentId);
  const {
    left: parentLeftPosition,
    top: parentTopPosition,
    right: parentRightPosition,
  } = parentElement ? parentElement.getBoundingClientRect() : {};

  useEffect(() => {
    const { position } = annotationPosition;

    const style = {
      marker: {
        ...getMarkerPosition({ markerRef, position, id, zoom, leftSideView }),
      },
      callout: {
        ...getCalloutPosition({
          calloutRef,
          position,
          id,
          zoom,
          leftSideView,
        }),
      },
    };

    const sectionsCache = leftSideView
      ? leftCache.get(sectionId)
      : cache.get(sectionId);

    sectionsCache.isLoaded && setAnnotationStyle(style);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fontSize,
    parentLeftPosition,
    parentTopPosition,
    parentRightPosition,
    sectionId,
    leftSideView,
    cache,
    leftCache,
    contentViewMode,
    elementSize,
    annotationPosition.position,
    elementData,
    zoom.zoomRatio,
    screenSize,
    isShowCallout,
    annotationDisplayFilter.showAll,
    annotationDisplayFilter.workpaper,
    annotationDisplayFilter.tickmarks,
    annotationDisplayFilter.internalRef,
    annotationDisplayFilter.formula,
    annotationDisplayFilter.comfortLetter,
    markerRef,
    calloutRef,
    tableControlsChanged,
    numberOfSourceElementSelected,
    numberOfTargetElementSelected,
  ]);

  return (
    <>
      {isShowCallout && elementData.callout && (
        <div
          ref={calloutRef}
          style={{
            position: 'absolute',
            ...annotationStyle.callout,
            width: 'fit-content',
            maxWidth: CALLOUT_MAX_WIDTH,
            zIndex: 52,
          }}
          className={classnames(
            `${ELEMENT_BLOCK}`,
            `${ELEMENT_BLOCK}__callout-container`,
            {
              [`${ELEMENT_BLOCK}__callout-container--left`]:
                (annotationPosition.position === 'left' &&
                  Object.prototype.hasOwnProperty.call(
                    annotationStyle.callout,
                    'left',
                  )) ||
                (annotationPosition.position === 'right' &&
                  Object.prototype.hasOwnProperty.call(
                    annotationStyle.callout,
                    'left',
                  )),
              [`${ELEMENT_BLOCK}__callout-container--right`]:
                (annotationPosition.position === 'right' &&
                  Object.prototype.hasOwnProperty.call(
                    annotationStyle.callout,
                    'right',
                  )) ||
                (annotationPosition.position === 'left' &&
                  Object.prototype.hasOwnProperty.call(
                    annotationStyle.callout,
                    'right',
                  )),
              [`${ELEMENT_BLOCK}__callout-container--bottom`]:
                annotationPosition.position === 'bottom',
              [`${ELEMENT_BLOCK}__callout-container--top`]:
                annotationPosition.position === 'top',
            },
          )}
          onClick={() => onAnnotationClick(false)}
          id={`${id}-${displayElementDetailsOnStatementPage}`}
        >
          <div className={`${ELEMENT_BLOCK}__callout-body`}>{children}</div>
        </div>
      )}
      {elementData.marker && !elementData.callout && (
        <span
          id={`${id}-annotation-marker`}
          ref={markerRef}
          style={{
            position: 'absolute',
            zIndex: 47,
            ...annotationStyle.marker,
            height: elementSize,
            width: elementSize,
            display: 'inline-flex',
          }}
          className={classnames(`${ELEMENT_BLOCK}__annotation-indicator`, {
            [`${ELEMENT_BLOCK}__annotation-indicator--left`]:
              annotationPosition.position === 'left',
            [`${ELEMENT_BLOCK}__annotation-indicator--right`]:
              annotationPosition.position === 'right',
            [`${ELEMENT_BLOCK}__annotation-indicator--bottom`]:
              annotationPosition.position === 'bottom',
          })}
          onClick={() => isShowCallout && onAnnotationClick(true)}
        >
          <AnnotationIndicator width={elementSize} height={elementSize} />
        </span>
      )}
    </>
  );
};

export default TieoutElementCallouts;
